/** Normalize DRF-style paginated payloads (or plain arrays) for list + Pagination UI. */
export const normalizePaginatedResponse = (payload) => {
    if (!payload) {
        return { results: [], count: 0, next: null, previous: null };
    }
    if (Array.isArray(payload)) {
        return { results: payload, count: payload.length, next: null, previous: null };
    }
    const results = payload.results ?? payload.records ?? [];
    const count =
        payload.count ??
        payload.total_count ??
        payload.total ??
        results.length;
    return { ...payload, results, count };
};

/** Parse query params from DRF next/previous URLs. */
export const parsePageFromUrl = (url) => {
    if (!url) return null;
    const match = String(url).match(/[?&]page=(\d+)/i);
    return match ? parseInt(match[1], 10) : null;
};

export const parsePageSizeFromUrl = (url) => {
    if (!url) return null;
    const match = String(url).match(/[?&]page_size=(\d+)/i);
    return match ? parseInt(match[1], 10) : null;
};

/**
 * Match the page size the API actually uses (may differ from the requested page_size).
 */
export const getEffectivePageSize = ({ pageSize, data }) => {
    const fromNext = parsePageSizeFromUrl(data?.next);
    const fromPrev = parsePageSizeFromUrl(data?.previous);
    if (fromNext != null) return fromNext;
    if (fromPrev != null) return fromPrev;

    const resultsLen = data?.results?.length ?? 0;
    if (data?.next && resultsLen > 0) {
        return resultsLen;
    }

    return Number(pageSize) || 10;
};

/**
 * Display page + total pages from total count and effective page size.
 */
export const getPaginationDisplay = ({ currentPage, pageSize, totalEntries, data }) => {
    const effectiveSize = getEffectivePageSize({ pageSize, data });
    const entries = Number(totalEntries) || 0;

    let totalPages = entries > 0 ? Math.ceil(entries / effectiveSize) : 1;
    if (!data?.next) {
        totalPages = Math.max(totalPages, currentPage);
    }
    totalPages = Math.max(1, totalPages, currentPage);

    const page = Math.min(Math.max(1, currentPage), totalPages);

    return { page, totalPages, pageSize: effectiveSize };
};

/** Wrap setState so Pagination `next`/`previous` links always get normalized rows + count. */
export const createNormalizedPageSetter = (setState) => (payload) => {
    if (typeof payload === 'function') {
        setState((prev) => normalizePaginatedResponse(payload(prev)));
    } else {
        setState(normalizePaginatedResponse(payload));
    }
};
